import { PropsWithChildren, useMemo, useState } from "react";
import { Stack } from "@altimateai/lego";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { useReactFlow } from "reactflow";
import { layoutElementsOnCanvas, moveTableFromSeeMoreToCanvas } from "../graph";
import { ColumnLineage, Table } from "../types";
import { CustomInput } from "./Form";
import { TestsIcon, EphemeralIcon, ExpandLineageIcon } from "../assets/icons";
import { NODE_TYPE_SHORTHAND, NodeIcon } from "./Column";
import { NODE_TYPE_STYLES, TableNodePill } from "./Column";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { setSidebarScreen } from "../context/LineageSlice";

export type TMoreTables = {
  prevTable?: string;
  tables?: Table[];
  right?: boolean;
  level?: number;
  lineage?: ColumnLineage[];
};

function TableHeader({
  nodeType,
  label,
  table,
  tests,
  materialization,
  canLineageExpand,
}: PropsWithChildren<{
  nodeType: string;
  label: string;
  table: string;
  tests: { key: string; path: string }[];
  materialization?: string | undefined;
  canLineageExpand: boolean;
}>) {
  const nType = nodeType as keyof typeof NODE_TYPE_SHORTHAND;
  const tableId = table.replace(/[^a-zA-Z0-9]/g, "-");
  return (
    <Stack direction="column" align="start" gap="xs" className="al-w-full">
      <div className={styles.table_header}>
        <div className={classNames(styles.node_icon, NODE_TYPE_STYLES[nType])}>
          <NodeIcon nodeType={nodeType} />
          <div>{NODE_TYPE_SHORTHAND[nType]}</div>
        </div>
        <div className="lines-2">{label}</div>
        {canLineageExpand && (
          <div className={classNames(styles.expand_lineage_icon, "al-w-6 al-h-6")}>
            <ExpandLineageIcon />
          </div>
        )}
      </div>
      {(tests?.length > 0 || materialization) && (
        <Stack direction="row" gap="xs" className={classNames(styles.node_extra_info)}>
          {tests?.length > 0 && (
            <TableNodePill
              id={"table-node-tests-" + tableId}
              icon={<TestsIcon />}
              text={tests.length.toString()}
              label="Tests"
            />
          )}
          {materialization && (
            <TableNodePill
              id={"table-node-materilization-" + tableId}
              icon={<EphemeralIcon />}
              text={materialization}
              label="Materialization"
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}

function MoreTables() {
  const {
    state: { moreTables, selectCheck, nonSelectCheck },
    rerender,
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  const { tables: _tables, level, lineage } = moreTables as TMoreTables;
  const flow = useReactFlow();

  const tables = useMemo(() => {
    const _lineageTables = new Set();
    for (const item of lineage || []) {
      _lineageTables.add(item.source[0]);
      _lineageTables.add(item.target[0]);
    }
    return _tables
      ?.map(t => ({ ...t, canLineageExpand: _lineageTables.has(t.table) }))
      ?.sort((a, b) => (a.canLineageExpand ? -1 : b.canLineageExpand ? 1 : 0));
  }, [_tables, lineage]);

  const onItemClick = async (_table: Table) => {
    const nodes = [...flow.getNodes()];
    const edges = [...flow.getEdges()];
    const allTablesAdded = moveTableFromSeeMoreToCanvas(nodes, edges, _table, moreTables, {
      direct: selectCheck,
      indirect: nonSelectCheck,
    });
    if (allTablesAdded) {
      lineageDispatch(setSidebarScreen(""));
    }

    layoutElementsOnCanvas(nodes, edges);
    flow.setNodes(nodes);
    flow.setEdges(edges);
    rerender();
  };

  const selectAllTables = () => {
    if (!tables || tables.length === 0) return;

    const nodes = [...flow.getNodes()];
    const edges = [...flow.getEdges()];

    // Process all tables that aren't already on the canvas
    let allTablesAdded = true;
    for (const table of tables) {
      const _node = flow.getNode(table.table);
      const isNodeOnOtherLevel = _node && _node.data.level !== level;

      if (!_node && !isNodeOnOtherLevel) {
        const result = moveTableFromSeeMoreToCanvas(nodes, edges, table, moreTables, {
          direct: selectCheck,
          indirect: nonSelectCheck,
        });
        if (!result) {
          allTablesAdded = false;
        }
      }
    }

    if (allTablesAdded) {
      lineageDispatch(setSidebarScreen(""));
    }

    layoutElementsOnCanvas(nodes, edges);
    flow.setNodes(nodes);
    flow.setEdges(edges);
    rerender();
  };

  const [filteredTables, setFilteredTables] = useState(tables);
  return (
    <Stack direction="column" className="p-2 al-h-full">
      <Stack direction="row" justify="between" align="center" className="mb-2">
        <div className="fw-semibold fs-5">Tables</div>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={selectAllTables}
          disabled={!filteredTables || filteredTables.length === 0}
        >
          Select All
        </button>
      </Stack>
      <CustomInput
        placeholder="Search by table name"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const _search = e.target.value.toLowerCase();
          setFilteredTables(tables!.filter(t => t.table.toLowerCase().includes(_search)));
        }}
      />
      <div className="mb-3" />
      <div className="al-h-full al-overflow-y">
        <Stack direction="column" gap="sm">
          {filteredTables!.map(t => {
            const _node = flow.getNode(t.table);
            const isNodeOnOtherLevel = _node && _node.data.level !== level;
            return (
              <div
                key={t.table}
                className={classNames(styles.table_card, {
                  [styles.selected]: _node,
                  // [styles.disabled]: isNodeOnOtherLevel,
                })}
                onClick={e => {
                  e.stopPropagation();
                  if (isNodeOnOtherLevel) return;
                  onItemClick(t);
                }}
              >
                <TableHeader
                  nodeType={t.nodeType}
                  label={t.label}
                  table={t.table}
                  tests={t.tests}
                  materialization={t.materialization}
                  canLineageExpand={t.canLineageExpand}
                />
              </div>
            );
          })}
        </Stack>
      </div>
    </Stack>
  );
}

export { MoreTables };
